<script lang="ts">
	import { browser } from '$app/env';
	import type { ChildFeatureLink, KeepExploringCategory } from '$lib/api/velatlas/response.types';
	import { createLazyLoadAttachment } from '$lib/attachments/lazyLoad';
	import FullPlaceDetails from '$lib/components/city/feature/FullPlaceDetails.svelte';
	import CollectionCard from '$lib/components/ui/collection/CollectionCard.svelte';
	import DiscoveryCard from '$lib/components/ui/DiscoveryCard.svelte';
	import ExpandedFeatureCard from '$lib/components/ui/ExpandedFeatureCard.svelte';
	import FactsCard from '$lib/components/ui/factsCard/FactsCard.svelte';
	import FaqList from '$lib/components/ui/FaqList.svelte';
	import HeroBgImage from '$lib/components/ui/HeroBgImage.svelte';
	import type { TypeMapPoints } from '$lib/components/ui/map/_other/typePoints';
	import QuickStat, { QuickStatSeparator } from '$lib/components/ui/QuickStat.svelte';
	import KeepExploringSection from '$lib/components/ui/section/KeepExploringSection.svelte';
	import Story from '$lib/components/ui/Story.svelte';
	import Videos from '$lib/components/ui/Videos.svelte';
	import type { BreadcrumbLink } from '$lib/features/_other/breadcrumbLink';
	import type { Faq } from '$lib/features/_other/faq';
	import type { NearbyFeature } from '$lib/features/_other/nearbyFeature';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import type { CityFeature, CityFeatureMinimal, CityFeaturePreview } from '$lib/features/cityFeature/cityFeature.type';
	import {
		ALSO_FEATURED_COPY,
		CITY_FEATURE_TYPE_ICON_MAP,
		CITY_FEATURE_TYPE_LABELS,
		CITY_FEATURE_TYPE_SINGLE_LABELS,
		CityFeatureType
	} from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { CollectionPreview } from '$lib/features/collection/model/collectionPreview';
	import type { FeatureCollections } from '$lib/features/collection/model/featureCollections';
	import type { Language } from '$lib/features/country/language/language.type';
	import type { CoverImage } from '$lib/features/image/imageDetails.type';
	import { WIFI_ACCESS_LABELS, type WifiAccess } from '$lib/features/place/place.type';
	import type { TimelineEvent } from '$lib/features/timelineEvent/timelineEvent.type';
	import { UrlUtil, type NavLink } from '$lib/utils/url.util';
	import { type Component, type Snippet } from 'svelte';
	import IconArrowRight from '~icons/mdi/arrow-right';
	import IconNearMe from '~icons/mdi/near-me';
	import IconPartyPopper from '~icons/mdi/party-popper';

	interface CollectionGroup {
		collection: CollectionPreview;
		index: number;
		nav: NavLink;
		countryNav: NavLink | null;
	}

	const {
		location,
		feature,
		children,
		nearbyFeatures,
		otherSameTypeItems,
		featureCollections,
		toursAndItinerariesContainingFeature,
		typeMapPoints,
		keepExploring,
		facts,
		story,
		events,
		faqs,
		language,
		breadcrumbs,
		ExtraQuickStatsSnippet
	}: {
		location: PlaceLocation;
		feature: CityFeature;
		otherSameTypeItems: CityFeatureMinimal[];
		children: ChildFeatureLink[];
		nearbyFeatures: NearbyFeature[];
		featureCollections: FeatureCollections;
		toursAndItinerariesContainingFeature: CityFeaturePreview[];
		typeMapPoints: TypeMapPoints;
		keepExploring: KeepExploringCategory[];
		facts: string[];
		story: string;
		events: TimelineEvent[];
		faqs: Faq[];
		language: Language;
		breadcrumbs: BreadcrumbLink[];
		ExtraQuickStatsSnippet?: Snippet;
	} = $props();

	let loadMap = $state<boolean>(false);

	const collectionGroups = $derived<CollectionGroup[]>(
		featureCollections.all.map((collection, i) => {
			const countryMatch = featureCollections.country.find((c) => c.metadata.slug === collection.metadata.slug);
			return {
				collection,
				index: i,
				nav: UrlUtil.toAllCollection({ slug: collection.metadata.slug }),
				countryNav: countryMatch
					? UrlUtil.toCountryCollection({ countrySlug: location.country.slug, collectionSlug: collection.metadata.slug })
					: null
			};
		})
	);

	const observeMapLoading = createLazyLoadAttachment(() => (loadMap = true));
</script>

<div class="flex min-h-screen flex-col gap-0">
	<HeroBgImage
		{breadcrumbs}
		header={{
			title: feature.englishName,
			description: feature.description
		}}
		imageGallery={{ images: feature.images, internalClasses: 'lg:h-[65vh]', ariaLabel: `View ${feature.englishName} gallery` }}
		decorations={{ coordinates: feature.place?.coordinates !== undefined ? [feature.place.coordinates] : [] }}
		navsToOtherPages={children.map((child) => ({
			Icon: CITY_FEATURE_TYPE_ICON_MAP[child.type],
			label: ALSO_FEATURED_COPY[child.type]!,
			nav: UrlUtil.toCityFeature({ location, type: child.type, featureSlug: child.slug }),
			ariaLabel: `${ALSO_FEATURED_COPY[child.type]!} – see ${feature.englishName} as a ${CITY_FEATURE_TYPE_SINGLE_LABELS[child.type].toLowerCase()}`
		}))}
	>
		{#snippet QuickStatsSnippet()}
			{#if feature.nativeName}
				<QuickStat
					value={feature.nativeName}
					valueNativeLanguage={{ languageCode: language.code, isLanguageRTL: language.rtl }}
				/>
			{/if}

			{#if feature.place?.elevationMeters}
				{@render QuickStatSeparator()}
				<QuickStat
					title="Elevation above sea level: {feature.place.elevationMeters} meters"
					label="ELEVATION"
					value={`${feature.place.elevationMeters}m`}
				/>
			{/if}

			{#if feature.place?.inceptionYear}
				{const age = $derived<number>(new Date().getFullYear() - feature.place.inceptionYear)}
				{@render QuickStatSeparator()}
				<QuickStat
					title="Inception year: {feature.place.inceptionYear} making it {age} years old"
					label="FOUNDED"
					value={feature.place.inceptionYear}
				/>
				{@render QuickStatSeparator()}
				<QuickStat label="YEARS OLD" value={age} />
			{/if}

			{#if feature.place?.exhibitionSize}
				{const formattedValue = $derived<string>(new Intl.NumberFormat().format(feature.place.exhibitionSize))}
				{@render QuickStatSeparator()}
				<QuickStat title="Exhibition size of {formattedValue}~ items" label="ITEMS" value={formattedValue} />
			{/if}

			{#if feature.place?.cost}
				{@render QuickStatSeparator()}
				<QuickStat
					title="Cost of {feature.place.cost.amount} {feature.place.cost.currencyCode}"
					label={feature.place.cost.currencyCode}
					value={feature.place.cost.amount}
				/>
			{/if}

			{#if feature.place?.visitorsPerYear}
				{const asOfYear = $derived<number | null>(feature.place.visitorsPerYearAsOf)}
				{const formattedValue = $derived<string>(new Intl.NumberFormat().format(feature.place.visitorsPerYear))}
				{@render QuickStatSeparator()}
				<QuickStat
					title="{formattedValue} visitors{asOfYear ? ` as of ${asOfYear}` : ''}"
					label={`VISITORS ${asOfYear ? `(${asOfYear})` : ''}`}
					value={formattedValue}
				/>
			{/if}

			{#if feature.place?.wifiAccess}
				{const access = $derived<WifiAccess>(feature.place.wifiAccess)}
				{@render QuickStatSeparator()}
				<QuickStat label="WI-FI" value={WIFI_ACCESS_LABELS[access]} />
			{/if}

			{#if ExtraQuickStatsSnippet}
				{@render QuickStatSeparator()}
				{@render ExtraQuickStatsSnippet()}
			{/if}
		{/snippet}
	</HeroBgImage>

	{@render OnPageLinks()}

	<div class="relative container mx-auto flex flex-col gap-7 px-4 md:flex-row">
		<div class="flex min-w-0 flex-1 flex-col gap-14">
			{#if feature.videos.length > 0}
				{@render Section(`Videos`, null, VideosSnippet)}
			{/if}

			{@render Section(`History of ${feature.englishName}`, null, StorySnippet)}

			{#if facts.length > 0}
				{@render Facts()}
			{/if}

			<FullPlaceDetails place={feature.place} initiallyOpen={true} />

			{#if feature.place}
				{@render Section(
					`${feature.englishName} Map & Neighborhood Explorer`,
					`This interactive map is centered on ${feature.englishName}. Zoom in and move the map around to automatically discover Wikipedia photos and summaries of other nearby sights, monuments, and historical points of interest and stories in real-time!`,
					MapSnippet
				)}
			{/if}

			{#if faqs.length > 0}
				{@render Section(`${feature.englishName} FAQ`, null, FaqDisplay)}
			{/if}

			{#if nearbyFeatures.length > 0}
				{@render Section(
					`Places near ${feature.englishName}`,
					`Make the most of your trip. Check out these top-rated spots located nearby.`,
					NearbyPlacesSnippet
				)}
			{/if}

			{#if collectionGroups.length > 0}
				{const title = $derived<string>(
					collectionGroups.length === 1
						? 'Explore Similar Places in This Collection'
						: 'Explore Similar Places in These Collections'
				)}
				{@render Section(title, null, CollectionsList)}
			{/if}

			{#if toursAndItinerariesContainingFeature.length > 0}
				{const title = $derived.by<string>(() => {
					if (toursAndItinerariesContainingFeature.length === 1) {
						const route: CityFeaturePreview = toursAndItinerariesContainingFeature[0];
						if (route.type === CityFeatureType.ITINERARY) {
							return `Featured on This Itinerary`;
						} else {
							return `Featured on This Route`;
						}
					}

					const uniqueTypes: CityFeatureType[] = [
						...Array.from(new Set(toursAndItinerariesContainingFeature.map((f) => f.type).values()))
					];

					if (uniqueTypes.length === 1) {
						const soloType: CityFeatureType = uniqueTypes[0];
						if (soloType === CityFeatureType.ITINERARY) {
							return `Featured on These Itineraries`;
						} else {
							return `Featured on These Routes`;
						}
					}

					return `Featured on These Itineraries and Routes`;
				})}
				{@render Section(title, null, ToursAndItinerariesContainingFeatureList)}
			{/if}

			{#if otherSameTypeItems.length > 0}
				{@render CategoryExitBanner()}
			{/if}

			<KeepExploringSection {location} categories={keepExploring} sectionClass="border-t border-base-content/10" />
		</div>
	</div>
</div>

{#snippet OnPageLinks()}
	{const navs = $derived<{ label: string; nav: NavLink }[]>([
		{
			label: 'History',
			nav: UrlUtil.toCityFeature({ location, type: feature.type, featureSlug: feature.slug, section: 'history' })
		},
		{
			label: 'Facts',
			nav: UrlUtil.toCityFeature({ location, type: feature.type, featureSlug: feature.slug, section: 'facts' })
		},
		{
			label: 'Map',
			nav: UrlUtil.toCityFeature({ location, type: feature.type, featureSlug: feature.slug, section: 'map' })
		},
		{
			label: 'FAQ',
			nav: UrlUtil.toCityFeature({ location, type: feature.type, featureSlug: feature.slug, section: 'faq' })
		},
		{
			label: 'Nearby Places',
			nav: UrlUtil.toCityFeature({ location, type: feature.type, featureSlug: feature.slug, section: 'nearby' })
		}
	])}

	<nav
		class="sticky top-14 z-40 mb-7 border-y border-base-content/10 bg-base-100/95 shadow-sm backdrop-blur-xl md:static md:z-auto md:bg-transparent md:shadow-none md:backdrop-blur-none"
		aria-label="On this page"
	>
		<div class="container mx-auto flex items-center gap-4 px-4 py-3">
			<span class="hidden shrink-0 font-mono text-xs font-bold tracking-[0.18em] text-base-content/70 sm:inline">
				ON THIS PAGE
			</span>

			<div
				class="flex min-w-0 flex-1 scrollbar-none items-center gap-2 overflow-x-auto whitespace-nowrap [&::-webkit-scrollbar]:hidden"
			>
				{#each navs as nav (nav.label)}
					<a
						class="btn btn-soft font-mono shadow-sm btn-primary btn-sm"
						href="/{nav.nav.href}"
						data-sveltekit-preload-data={nav.nav.preloadData}
						data-sveltekit-preload-code={nav.nav.preloadCode}
						aria-label="{nav.label} – see {feature.englishName} as a {CITY_FEATURE_TYPE_SINGLE_LABELS[
							feature.type
						].toLowerCase()}"
					>
						{nav.label}
					</a>
				{/each}
			</div>
		</div>
	</nav>
{/snippet}

{#snippet StorySnippet()}
	<Story {story} {events} />
{/snippet}

{#snippet VideosSnippet()}
	<Videos videos={feature.videos} englishName={feature.englishName} />
{/snippet}

{#snippet MapSnippet()}
	<div {@attach observeMapLoading} id="map" class="rounded-box shadow-sm">
		{#if loadMap}
			<figure aria-label="Map of {feature.englishName}">
				<div
					class="relative z-0 h-[50vh] w-full overflow-hidden rounded-box border border-base-content/5 shadow-inner md:h-[60vh]"
				>
					{#if browser}
						{#await import('$lib/components/ui/map/SinglePointMap.svelte') then { default: SinglePointMap }}
							<SinglePointMap primaryPoint={feature.place!.coordinates} {typeMapPoints} />
						{/await}
					{:else}
						<div class="flex h-full w-full items-center justify-center bg-base-200 text-base-content/70">
							<span class="loading mr-2 loading-spinner text-primary"></span>
							<span>Loading map of {feature.englishName}...</span>
						</div>
					{/if}
				</div>
				<figcaption class="sr-only">
					A non-interactive map showing the location of {feature.englishName} in {location.city.label}, {location.country.label}.
				</figcaption>
			</figure>
		{:else}
			<div class="flex h-full w-full items-center justify-center text-base-content/70">
				<span class="loading mr-2 loading-spinner text-primary"></span>
				<span>Loading map of {feature.englishName}...</span>
			</div>
		{/if}
	</div>
{/snippet}

{#snippet Facts()}
	<FactsCard
		id="facts"
		sectionTitle="Fun Facts"
		Icon={IconPartyPopper}
		facts={facts.map((fact) => ({ content: fact }))}
		initialCount={4}
	/>
{/snippet}

{#snippet FaqDisplay()}
	<FaqList {faqs} />
{/snippet}

{#snippet ToursAndItinerariesContainingFeatureList()}
	<ul class="grid list-none grid-cols-1 gap-8 p-0 md:grid-cols-3">
		{#each toursAndItinerariesContainingFeature as feature (feature.id)}
			{const images = $derived<CoverImage[]>(feature.images)}
			<li>
				<ExpandedFeatureCard {feature} {images} {location} descriptionLineClampNumber={2} />
			</li>
		{/each}
	</ul>
{/snippet}

{#snippet CollectionsList()}
	<ul class="grid list-none grid-cols-1 gap-8 p-0 md:grid-cols-2 lg:grid-cols-3">
		{#each collectionGroups as { collection, index, nav, countryNav } (collection.metadata.slug)}
			{const overridenCollection = $derived<CollectionPreview>({
				...collection,
				metadata: { ...collection.metadata, collectionTitle: collection.metadata.pageTitle }
			})}
			<li class="flex flex-col items-center gap-3">
				<CollectionCard collection={overridenCollection} {index} {nav} />

				{#if countryNav}
					<a
						href="/{countryNav.href}"
						data-sveltekit-preload-data={countryNav.preloadData}
						data-sveltekit-preload-code={countryNav.preloadCode}
						class="group/pill inline-flex items-center justify-center gap-2 rounded-full border border-base-300 bg-base-100 py-2 pr-3 pl-4 font-mono text-sm font-medium text-primary shadow-sm transition-colors duration-200 hover:bg-base-200"
					>
						or explore {collection.metadata.collectionTitle.toLowerCase()} in {location.country.label}
						<IconArrowRight class="size-3.5 transition-transform duration-200 group-hover/pill:translate-x-1" />
					</a>
				{/if}
			</li>
		{/each}
	</ul>
{/snippet}

{#snippet NearbyPlacesSnippet()}
	<section id="nearby" class="scroll-mt-34">
		<ul class="grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2 lg:grid-cols-4">
			{#each nearbyFeatures as item (item.feature.id)}
				<li>
					<DiscoveryCard
						elementId={item.feature.id}
						englishName={item.feature.englishName}
						nativeName={null}
						description={item.feature.description}
						itemType={item.feature.type}
						{language}
						images={item.feature.images}
						nav={UrlUtil.toCityFeature({ location, type: item.feature.type, featureSlug: item.feature.slug })}
						badge={{ text: `${item.distanceMeters}m`, Icon: IconNearMe }}
						NoImagePlaceholderIcon={CITY_FEATURE_TYPE_ICON_MAP[item.feature.type]}
					/>
				</li>
			{/each}
		</ul>
	</section>
{/snippet}

{#snippet CategoryExitBanner()}
	{const coverImages = $derived<CoverImage[]>(
		otherSameTypeItems
			.flatMap((item) => item.image)
			.filter((img) => img !== undefined)
			.slice(0, 8)
	)}
	{const Icon = $derived<Component>(CITY_FEATURE_TYPE_ICON_MAP[feature.type])}
	{const label = $derived<string>(CITY_FEATURE_TYPE_LABELS[feature.type])}
	{const nav = $derived<NavLink>(UrlUtil.toCityFeatures({ location, type: feature.type }))}

	<a
		href={`/${nav.href}`}
		data-sveltekit-preload-data={nav.preloadData}
		data-sveltekit-preload-code={nav.preloadCode}
		class="group relative flex flex-col gap-6 rounded-box border border-base-content/10 bg-base-300 p-6 shadow-xs transition-all duration-300 hover:border-primary/40 hover:bg-base-200 hover:shadow-md sm:flex-row sm:items-center sm:justify-between md:p-8"
	>
		<div class="flex items-start gap-4 text-left">
			<div class="flex flex-col gap-2">
				<div class="flex flex-row items-center gap-1 text-primary">
					<Icon class="text-sm" />
					<span class="text-xs font-bold tracking-widest">CONTINUE EXPLORING</span>
				</div>

				<h3 class="text-xl transition-colors group-hover:text-primary sm:text-2xl">
					See all {label.toLowerCase()} in {location.city.label}
				</h3>

				{const sample = $derived<string[]>(otherSameTypeItems.slice(0, 3).map((item) => item.englishName))}
				{const remainingCount = $derived<number>(otherSameTypeItems.length - sample.length)}

				<p class="line-clamp-2 text-xs font-light text-base-content/70 md:line-clamp-1">
					<i>Including {sample.join(', ')}{remainingCount > 0 ? `, and more` : ''}</i>
				</p>
			</div>
		</div>

		<div class="flex w-full flex-col gap-5 sm:w-auto sm:flex-row sm:items-center sm:justify-end sm:gap-6">
			{#if coverImages.length > 0}
				<div class="flex -space-x-4 self-start transition-all duration-300 group-hover:-space-x-1.5 sm:self-auto">
					{#each coverImages as img (img.filename)}
						<div
							class="relative size-12 shrink-0 overflow-hidden rounded-2xl shadow-sm ring-2 ring-base-100 transition-all duration-300 ease-out sm:size-14"
						>
							<img
								class="h-full w-full object-cover"
								src={UrlUtil.getFullImageUrl(img)}
								alt={img.altText}
								width={img.width}
								height={img.height}
								loading="lazy"
							/>
						</div>
					{/each}
				</div>
			{/if}

			<div
				class="btn w-full shrink-0 rounded-full px-6 font-mono text-xs font-semibold shadow-xs transition-all btn-primary sm:w-auto sm:btn-md"
			>
				<span>browse {label.toLowerCase()}</span>
				<IconArrowRight class="size-4 transition-transform duration-200 group-hover:translate-x-1" />
			</div>
		</div>
	</a>
{/snippet}

{#snippet Section(title: string, description: string | null, Snippet: Snippet)}
	<section class="flex flex-col gap-5">
		<div>
			<h2 class="text-2xl">{title}</h2>
			{#if description}
				<p class="mt-1 text-sm text-base-content/70">{description}</p>
			{/if}
		</div>
		{@render Snippet()}
	</section>
{/snippet}
